// Toast.tsx
import React, { useEffect, useState } from 'react';
import { ReactComponent as CloseIcon } from 'assets/icons_3/x-close.svg';
import { ReactComponent as SuccessIcon } from 'assets/icons_3/tick-circle.svg';
import { ReactComponent as DangerIcon } from 'assets/icons_3/danger-circle.svg';
import styles from './Toast.module.scss';
import Button from '../Button/Button';

interface ToastProps {
  key: string;
  title?: string;
  message?: string;
  variant?: 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light';
  autohide?: boolean;
  delay?: number;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  key,
  title,
  message,
  variant = 'primary',
  autohide = false,
  delay = 5000,
  onClose,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  useEffect(() => {
    if (autohide) {
      const timer = setTimeout(() => {
        handleClose();
      }, delay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autohide, delay]);

  const getVariantIcon = () => {
    if (variant === 'success') return <SuccessIcon />;
    if (variant === 'danger') return <DangerIcon />;
    if (variant === 'warning') return <DangerIcon />;
    // if (variant === 'info') return <InfoIcon />;
    // Для остальных вариантов не показываем иконку
    return null;
  };

  return (
    <div key={key} className={`${styles.toast} ${styles[variant]} ${isExiting ? styles.exiting : ''}`}>
      {title && (
        <div className={styles.toastHeader}>
          {getVariantIcon() && (
          <div className={styles.icon}>
            {getVariantIcon()}
          </div>
          )}
          <span className={styles.title}>{title}</span>
          <Button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
            variant="link-gray"
          >
            <CloseIcon />
          </Button>
        </div>
      )}
      <div className={styles.toastBody}>
        {message}
      </div>
    </div>
  );
};

interface ToastContainerProps {
  children: React.ReactNode;
  position?: 'top-start' | 'top-center' | 'top-end' | 'bottom-start' | 'bottom-center' | 'bottom-end';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  children,
  position = 'top-end',
}) => {
  const positionClass = position.replace(/-/g, '');
  const capitalizedPosition = positionClass.charAt(0).toUpperCase() + positionClass.slice(1);
  const styleKey = positionClass.charAt(0).toLowerCase() + capitalizedPosition.slice(1);

  return (
    <div className={`${styles.toastContainer} ${styles[styleKey]}`} qa-element="toast">
      {children}
    </div>
  );
};
